import React, { useMemo } from 'react';

import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';

import { formatDateSlashesWithClock } from '~/services/TimeService';

import type { ParityNotificationRecord } from '~/types/services/parityNotifications';

interface LowInventoryNotificationsGridProps {
  notifications: ParityNotificationRecord[];
  isLoading: boolean;
  error: Error | null;
  onSendEmail: (id: string) => void;
  sendingId: string | null;
}

function buildLowInventoryColumns(
  onSendEmail: (id: string) => void,
  sendingId: string | null,
): GridColDef<ParityNotificationRecord>[] {
  return [
    { field: 'id', headerName: 'ID', flex: 2 },
    { field: 'fk_vendor_id', headerName: 'Vendor ID', flex: 1.5 },
    { field: 'fk_salesforce_id', headerName: 'Case ID', flex: 1.5 },
    {
      field: 'offerId',
      headerName: 'Offer ID',
      flex: 1.5,
      valueGetter: (_value, row) => row.metadata?.offerId ?? '',
    },
    {
      field: 'propertyId',
      headerName: 'Property ID',
      flex: 1.5,
      valueGetter: (_value, row) => row.metadata?.propertyId ?? '',
    },
    {
      field: 'months',
      headerName: 'Months',
      flex: 1,
      valueGetter: (_value, row) => (row.metadata?.months ?? []).join(', '),
    },
    {
      field: 'threshold',
      headerName: 'Threshold %',
      flex: 0.75,
      valueGetter: (_value, row) => row.metadata?.threshold ?? '',
    },
    {
      field: 'leadRatePlan',
      headerName: 'Lead Rate Plan',
      flex: 1.5,
      valueGetter: (_value, row) => row.metadata?.lead_rate_plan_name ?? '',
    },
    { field: 'status', headerName: 'Status', flex: 0.75 },
    {
      field: 'created_at',
      headerName: 'Created At',
      flex: 1.5,
      valueFormatter: (value) => (value ? formatDateSlashesWithClock(value) : ''),
    },
    {
      field: 'actions',
      headerName: '',
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => (
        <Button size="small" variant="text" disabled={sendingId === row.id} onClick={() => onSendEmail(row.id)}>
          {sendingId === row.id ? <CircularProgress size={14} /> : 'Send Email'}
        </Button>
      ),
    },
  ];
}

export default function LowInventoryNotificationsGrid({
  notifications,
  isLoading,
  error,
  onSendEmail,
  sendingId,
}: LowInventoryNotificationsGridProps) {
  const columns = useMemo(() => buildLowInventoryColumns(onSendEmail, sendingId), [onSendEmail, sendingId]);

  return (
    <Box mt={4}>
      <Typography variant="h6" mb={1}>
        Unsolved Low Inventory Notifications
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}

      <DataGrid
        autoHeight
        columns={columns}
        rows={notifications}
        loading={isLoading}
        getRowId={(row) => row.id}
        disableRowSelectionOnClick
        disableColumnMenu
      />
    </Box>
  );
}
